import { isPayablePaymentLinkStatus, PAYMENT_LINK_ANOMALY_LABELS } from '@gozoki/api-types'
import { DateTime } from 'luxon'
import { getTuyauErrorStatus, getTuyauRetryAfterSeconds } from '~/lib/utils'
import type { PaymentLinkChannel, PaymentLinkStatus } from './payment-link-types'

/**
 * Lecture d'un lien de paiement dans le BO (kanban #870, lot 7) : statut effectif, libellés,
 * anomalies, actions possibles. Pur et testé (`payment-link-status.test.mjs`) : le statut et les
 * boutons Renvoyer / Annuler sont des informations de décision.
 */

export interface PaymentLinkLike {
  status: PaymentLinkStatus
  expiresAt: string
}

/**
 * Statut à afficher : un lien encore payable dont la date limite est passée se lit « expiré »
 * sans attendre le job d'expiration (le backend refuse déjà de le renvoyer et la page publique de
 * l'ouvrir).
 */
export const effectivePaymentLinkStatus = (
  link: PaymentLinkLike,
  now: Date = new Date(),
): PaymentLinkStatus => {
  if (!isPayablePaymentLinkStatus(link.status)) return link.status
  const expires = DateTime.fromISO(link.expiresAt)
  return expires.isValid && expires.toMillis() <= now.getTime() ? 'expired' : link.status
}

export type PaymentLinkTone = 'positive' | 'info' | 'warning' | 'negative' | 'neutral'

export interface PaymentLinkStatusPresentation {
  label: string
  tone: PaymentLinkTone
}

const STATUS_PRESENTATIONS: Record<PaymentLinkStatus, PaymentLinkStatusPresentation> = {
  created: { label: 'Créé', tone: 'neutral' },
  sent: { label: 'Envoyé', tone: 'info' },
  opened: { label: 'Ouvert', tone: 'info' },
  paid: { label: 'Payé', tone: 'positive' },
  expired: { label: 'Expiré', tone: 'neutral' },
  cancelled: { label: 'Annulé', tone: 'neutral' },
  failed: { label: "Échec d'envoi", tone: 'negative' },
}

/** Variante de `Badge` de chaque tonalité : seule table, partagée par la fiche et Clients bannis. */
export const PAYMENT_LINK_BADGE_VARIANT: Record<
  PaymentLinkTone,
  'success' | 'outline' | 'gray' | 'destructive'
> = {
  positive: 'success',
  info: 'outline',
  warning: 'outline',
  negative: 'destructive',
  neutral: 'gray',
}

export const paymentLinkStatusPresentation = (
  status: PaymentLinkStatus,
): PaymentLinkStatusPresentation => STATUS_PRESENTATIONS[status]

/**
 * Anomalies de règlement : l'argent est encaissé, un humain doit regarder. Libellés du contrat
 * partagé (`@gozoki/api-types`), les mêmes que les alertes email et les lignes de paiement.
 */
export { PAYMENT_LINK_ANOMALY_LABELS }

/** « email + SMS », « SMS » ; « — » sans canal. */
export const formatPaymentLinkChannels = (channels: readonly PaymentLinkChannel[]): string =>
  channels.map((channel) => (channel === 'sms' ? 'SMS' : 'email')).join(' + ') || '—'

export const canResendPaymentLink = (link: PaymentLinkLike, now: Date = new Date()): boolean =>
  isPayablePaymentLinkStatus(effectivePaymentLinkStatus(link, now))

export const canCancelPaymentLink = canResendPaymentLink

/** `24/09/2026 à 15:30`. */
export const formatPaymentLinkDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const parsed = DateTime.fromISO(iso)
  return parsed.isValid ? parsed.setLocale('fr').toFormat("dd/MM/yyyy 'à' HH:mm") : '—'
}

export interface PaymentLinkTimelineSource extends PaymentLinkLike {
  sentAt: string | null
  openedAt: string | null
  paidAt: string | null
}

/**
 * Étapes datées d'un lien, dans l'ordre : envoyé, ouvert, payé, puis la date limite (« expire
 * le » tant qu'elle est à venir, « expiré le » ensuite, rien pour un lien payé ou annulé).
 */
export const paymentLinkTimeline = (
  link: PaymentLinkTimelineSource,
  now: Date = new Date(),
): string[] => {
  const steps: string[] = []
  if (link.sentAt) steps.push(`envoyé le ${formatPaymentLinkDateTime(link.sentAt)}`)
  if (link.openedAt) steps.push(`ouvert le ${formatPaymentLinkDateTime(link.openedAt)}`)
  if (link.paidAt) steps.push(`payé le ${formatPaymentLinkDateTime(link.paidAt)}`)
  const status = effectivePaymentLinkStatus(link, now)
  if (status === 'expired') steps.push(`expiré le ${formatPaymentLinkDateTime(link.expiresAt)}`)
  else if (isPayablePaymentLinkStatus(status)) {
    steps.push(`expire le ${formatPaymentLinkDateTime(link.expiresAt)}`)
  }
  return steps
}

/**
 * Message du 429 du renvoi (« 1 renvoi / minute / lien », `{ error, retryAfter }`) : le délai
 * restant vaut plus qu'un « trop de tentatives » générique.
 */
export const resendRateLimitMessage = (error: unknown): string | null => {
  if (getTuyauErrorStatus(error) !== 429) return null
  const retryAfter = getTuyauRetryAfterSeconds(error)
  const delay = retryAfter === null ? "dans moins d'une minute" : `dans ${Math.ceil(retryAfter)} s`
  return `Ce lien vient d'être renvoyé : un renvoi par minute, réessayez ${delay}.`
}
